import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { useHomepage } from './HomepageContext';
import { Button, Typography } from './symphony/ui';
import { useProductName } from '../lib/use-brand';
import { useTranslation } from '../lib/i18n';
import styles from './SymphonyWelcomeModal.module.css';

// The one-time "Welcome to your new Symphony" notice — the web port of mobile's
// PR #5768 (features/root/show-symphony-rename-modal-once.ts). Shows once per
// browser, and never to a user who onboarded in this browser: they arrived on
// Symphony and have no previous version to be told about. That new-user exclusion
// mirrors mobile's trick of pre-burning the seen flag at first launch when there is
// no recipient yet — here the onboarding flow burns it (see the phone step).
// Copy is Neomi's, verbatim.

const SEEN_KEY = 'symphony-welcome-seen';

// The rename notice this replaces had its own flag, and product wants everyone who saw
// that one to get this. So the key is deliberately NEW — starting unset is what re-shows
// the notice — and the dead one is removed rather than left to sit in every returning
// user's storage forever. Its `orion-web:` prefix does not carry over: nothing is being
// migrated, so a new key is free to be named for the product.
const RETIRED_SEEN_KEY = 'orion-web:symphony-rename-seen';

export function hasSeenSymphonyWelcome(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true; // no storage → don't nag
  }
}

export function markSymphonyWelcomeSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // private mode — the in-session state below still suppresses a repeat
  }
}

function forgetRetiredNotice(): void {
  try {
    window.localStorage.removeItem(RETIRED_SEEN_KEY);
  } catch {
    // nothing to clean up without storage
  }
}

export function SymphonyWelcomeModal() {
  const { t } = useTranslation();
  const productName = useProductName();
  const [open, setOpen] = useState(false);

  useEffect(forgetRetiredNotice, []);

  // Wait for the app to finish loading before opening. Opening on mount put the notice up
  // over a half-built shell — the same load the user sees without it, only with the pane
  // scrimmed behind the card. It also made the flag unsafe: it is burned on open, so a
  // notice that opens while something still covers the app (the instance-auth loader sits
  // at z-100000) or while the auth check is redirecting to sign-in is spent without ever
  // being read. `useHomepage` settles on success AND on failure, so the wait is bounded;
  // if it somehow never settles, the notice stays unopened and unburned and comes back
  // next load, which is the right way round to fail.
  const ready = useHomepage().state.status !== 'loading';
  useEffect(() => {
    if (!ready) return;
    if (hasSeenSymphonyWelcome()) return;
    setOpen(true);
    markSymphonyWelcomeSeen();
  }, [ready]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={t('welcome.title', { productName })}
      labelledBy="symphony-welcome-title"
      // The default 440 leaves the two longest benefits 2–17px short of their line, so
      // each wrapped to a single orphaned word. Not chasing zero wrapping — the longest
      // translations want ~550 and a notice that wide reads wrong; this is the width at
      // which the copy has real headroom instead of a few pixels of it.
      width={480}
      maxHeightPx={650}
      scrollBody
      footer={<Button onClick={() => setOpen(false)}>{t('common.close')}</Button>}
    >
      <div className={styles.body}>
        {/* Its own level between the title and the list, which the copy names but the
            bullets' size and ink gave it no way to show. Weight and ink rather than size:
            at 16 it rivalled the 18/700 title and the two read as competing headings. */}
        <Typography variant="h4">{t('welcome.subtitle', { productName })}</Typography>
        <Typography variant="p2" color="secondary" component="ul" className={styles.benefits}>
          <li>{t('welcome.benefits.continuity')}</li>
          <li>{t('welcome.benefits.crossBusiness')}</li>
          <li>{t('welcome.benefits.dailyUpdates')}</li>
          <li>{t('welcome.benefits.dashboards')}</li>
          <li>{t('welcome.benefits.wixIntelligence')}</li>
        </Typography>
      </div>
    </Modal>
  );
}
